import type { IssueActionCountDto } from '@issuemanager/api-client';
import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useInfiniteIssueActionCount } from '@issuemanager/client/features/issues';
import { useMemo, useState } from 'react';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type TransformedIssueActionCountDto = IssueActionCountDto & {
  id: string;
};

export type IssueActionCountMultipleSelectorProps = MultipleSelectorProps<
  TransformedIssueActionCountDto[],
  TransformedIssueActionCountDto
>;

function issueActionCountDataTransformer(data: IssueActionCountDto) {
  return {
    ...data,
    id: String(data.actionCount),
  };
}

export function IssueActionCountMultipleSelector(
  props: IssueActionCountMultipleSelectorProps,
) {
  const { value: valueProp, onChange, enabled: _enabled, ...rest } = props;

  const value = useMemo(
    () => valueProp?.map(issueActionCountDataTransformer) ?? [],
    [valueProp],
  );

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value);

  const issueActionCountQuery = useInfiniteIssueActionCount({
    params: {
      filter: {
        searchTerm: filterByText,
      },
    },
    enabled: filterByText.length > 0,
  });

  const options = mergePaginatedDataAndValue(
    issueActionCountQuery.data?.pages.map((c) =>
      c.data.map(issueActionCountDataTransformer),
    ) ?? [],
    value,
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: issueActionCountQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      getOptionLabel={(option) => String(option.actionCount)}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
